import { MANAGED_METAS, type SiteHead } from '../../../shared/siteSeo';

/**
 * Makes the document's <head> describe the page on screen: the same head the server wrote into the first HTML
 * (shared/siteSeo.ts siteHead), applied again on every client navigation and plans revalidation. Tags the new page
 * has are created or updated; managed tags it lacks (a noindex left by /status or the not-found page, a canonical
 * or og:url on the not-found page) are removed; the JSON-LD is replaced.
 */
export function applySiteHead(head: SiteHead, doc: Document = document): void {
  doc.title = head.title;
  for (const { attr, key } of MANAGED_METAS) {
    const wanted = head.metas.find((m) => m.attr === attr && m.key === key);
    const existing = Array.from(doc.head.querySelectorAll<HTMLMetaElement>(`meta[${attr}="${key}"]`));
    if (!wanted) {
      existing.forEach((el) => el.remove());
      continue;
    }
    const [el, ...extra] = existing;
    extra.forEach((x) => x.remove());
    const tag = el ?? doc.head.appendChild(doc.createElement('meta'));
    tag.setAttribute(attr, key);
    tag.setAttribute('content', wanted.content);
  }

  const canonicals = Array.from(doc.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
  if (!head.canonical) canonicals.forEach((el) => el.remove());
  else {
    const [el, ...extra] = canonicals;
    extra.forEach((x) => x.remove());
    const link = el ?? doc.head.appendChild(doc.createElement('link'));
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', head.canonical);
  }

  doc.head.querySelectorAll('script[type="application/ld+json"]').forEach((el) => el.remove());
  if (head.jsonLd) {
    const script = doc.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(head.jsonLd);
    doc.head.appendChild(script);
  }
}
